import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridRowParams } from '@mui/x-data-grid';
import type React from 'react';
import type { PipelineData, SellerSummary } from 'services/types/insuranceDashboard';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

import PolicyFunnel from './PolicyFunnel';
import { PLAN_NAME_COLORS } from './policyStatusConstants';

const BLUE = '#2C5889';
const SURFACE_HOVER = '#eceaff';

interface Props {
  sellers: SellerSummary[];
  totals: { totalUF: number; policyCount: number };
  pipeline?: PipelineData;
}

const cardSx = {
  borderRadius: RADIUS_COIN,
  boxShadow: SHADOW,
  border: 'none',
  bgcolor: SURFACE_CONTAINER_LOWEST,
} as const;

const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.75rem',
  fontWeight: 600,
} as const;

const chartCardSx = {
  ...cardSx,
  flex: 1,
  minWidth: 350,
  p: 3,
} as const;

const getInitials = (name: string) => {
  const parts = name
    .replace(/^Vendedor\s*/i, '')
    .trim()
    .split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
};

const AVATAR_COLORS = [NAVY, BLUE, '#25616F', '#2A578B', '#47BCD8'];

const columns: GridColDef<SellerSummary>[] = [
  {
    field: 'userName',
    headerName: 'Vendedor',
    width: 240,
    renderCell: (params: GridRenderCellParams<SellerSummary, string>) => {
      const name = params.value || '';
      const colorIdx = (params.row.userId ?? 0) % AVATAR_COLORS.length;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: AVATAR_COLORS[colorIdx],
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {getInitials(name)}
          </Box>
          <Typography variant="body2" sx={{ color: NAVY, fontWeight: 500 }}>
            {name}
          </Typography>
        </Box>
      );
    },
  },
  { field: 'policyCount', headerName: 'Pólizas', width: 90, type: 'number' },
  {
    field: 'totalUF',
    headerName: 'Total UF',
    width: 110,
    type: 'number',
    valueFormatter: (value: number) => value.toFixed(2),
  },
  {
    field: 'quoteCount',
    headerName: 'Cotizaciones',
    width: 110,
    type: 'number',
  },
  {
    field: 'conversion',
    headerName: 'Conversión',
    width: 100,
    type: 'number',
    valueGetter: (_value: unknown, row: SellerSummary) =>
      row.totalPolicyCount > 0 ? Math.round((row.policyCount / row.totalPolicyCount) * 100) : 0,
    valueFormatter: (value: number) => `${value}%`,
  },
  {
    field: 'progress',
    headerName: 'Meta mensual',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: (params: GridRenderCellParams<SellerSummary, number>) => {
      const progress = params.row.goalProgress;
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(progress, 100)}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: SURFACE_CONTAINER_LOW,
                '& .MuiLinearProgress-bar': { backgroundColor: YELLOW, borderRadius: 6 },
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: NAVY,
              minWidth: 38,
              textAlign: 'right',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            {progress}%
          </Typography>
        </Box>
      );
    },
  },
];

const BusinessUnitAdminView: React.FC<Props> = ({ sellers, totals, pipeline }) => {
  const buEntries = Object.entries(pipeline?.policiesByBusinessUnit || {});
  const pieData = buEntries.map(([buName, count], idx) => ({
    id: idx,
    value: count,
    label: buName,
    color: PLAN_NAME_COLORS[idx % PLAN_NAME_COLORS.length],
  }));

  return (
    <Box>
      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 2.5,
          mb: 3,
        }}
      >
        {/* Total UF Cartera */}
        <Card sx={{ ...cardSx, position: 'relative', overflow: 'hidden' }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: `${YELLOW}15`,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: RADIUS_CARD,
                  bgcolor: `${YELLOW}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: YELLOW }} />
              </Box>
              <Typography variant="subtitle2" sx={labelSx}>
                Total UF Cartera
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: NAVY,
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.1,
                fontFamily: 'Jost, sans-serif',
                letterSpacing: '-0.02em',
              }}
            >
              {totals.totalUF.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: NAVY, opacity: 0.4, mt: 0.5, display: 'block' }}>
              UF
            </Typography>
          </CardContent>
        </Card>

        {/* Total Pólizas */}
        <Card sx={{ ...cardSx, position: 'relative', overflow: 'hidden' }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: `${BLUE}12`,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: RADIUS_CARD,
                  bgcolor: `${BLUE}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AssignmentOutlinedIcon sx={{ fontSize: 20, color: BLUE }} />
              </Box>
              <Typography variant="subtitle2" sx={labelSx}>
                Total Pólizas
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: NAVY,
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.1,
                fontFamily: 'Jost, sans-serif',
              }}
            >
              {totals.policyCount}
            </Typography>
          </CardContent>
        </Card>

        {/* Conversión */}
        <Card sx={{ ...cardSx, position: 'relative', overflow: 'hidden' }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(37, 97, 111, 0.08)',
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: RADIUS_CARD,
                  bgcolor: 'rgba(37, 97, 111, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 20, color: '#25616F' }} />
              </Box>
              <Typography variant="subtitle2" sx={labelSx}>
                Conversión
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: NAVY,
                fontSize: { xs: '2rem', md: '2.5rem' },
                lineHeight: 1.1,
                fontFamily: 'Jost, sans-serif',
              }}
            >
              {pipeline?.conversionRate ?? 0}%
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Funnel + BU Distribution */}
      {pipeline && (Object.keys(pipeline.policiesByStatus).length > 0 || pieData.length > 0) && (
        <Box sx={{ display: 'flex', gap: 2.5, mb: 3, flexWrap: 'wrap' }}>
          {Object.keys(pipeline.policiesByStatus).length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Embudo de pólizas
              </Typography>
              <PolicyFunnel policiesByStatus={pipeline.policiesByStatus} />
            </Card>
          )}
          {pieData.length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Distribución por Unidad de Negocio
              </Typography>
              <PieChart
                colors={PLAN_NAME_COLORS}
                series={[
                  {
                    data: pieData,
                    highlightScope: { fade: 'global', highlight: 'item' },
                    innerRadius: 50,
                  },
                ]}
                height={300}
                margin={{ bottom: 40 }}
                slotProps={{
                  legend: {
                    direction: 'horizontal',
                    position: { vertical: 'bottom', horizontal: 'center' },
                  },
                }}
              />
            </Card>
          )}
        </Box>
      )}

      {/* Plan + Brand charts */}
      {pipeline && (pipeline.policiesByPlanName?.length || pipeline.simulationsByBrand?.length) && (
        <Box sx={{ display: 'flex', gap: 2.5, mb: 3, flexWrap: 'wrap' }}>
          {pipeline.policiesByPlanName && pipeline.policiesByPlanName.length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Plan contratado
              </Typography>
              <PieChart
                series={[
                  {
                    data: pipeline.policiesByPlanName.map((d, idx) => ({
                      id: idx,
                      value: d.count,
                      label: d.planName,
                      color: PLAN_NAME_COLORS[idx % PLAN_NAME_COLORS.length],
                    })),
                    highlightScope: { fade: 'global', highlight: 'item' },
                    innerRadius: 50,
                    valueFormatter: (item) => `${item.value} pólizas`,
                  },
                ]}
                height={300}
                margin={{ bottom: 40 }}
                slotProps={{
                  legend: {
                    direction: 'horizontal',
                    position: { vertical: 'bottom', horizontal: 'center' },
                  },
                }}
              />
            </Card>
          )}
          {pipeline.simulationsByBrand && pipeline.simulationsByBrand.length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Cotizaciones por marca
              </Typography>
              <BarChart
                layout="horizontal"
                yAxis={[
                  {
                    scaleType: 'band',
                    data: pipeline.simulationsByBrand.map((d) => d.brand),
                    disableLine: true,
                    disableTicks: true,
                  },
                ]}
                xAxis={[{ disableLine: true, disableTicks: true, tickLabelStyle: { display: 'none' } }]}
                series={[
                  {
                    data: pipeline.simulationsByBrand.map((d) => d.simulationCount),
                    color: BLUE,
                    valueFormatter: (value: number | null) => `${value ?? 0} cotizaciones`,
                  },
                ]}
                height={Math.max(250, pipeline.simulationsByBrand.length * 40)}
                margin={{ left: 120, right: 40, bottom: 10 }}
                grid={{ vertical: false }}
                hideLegend={true}
              />
            </Card>
          )}
        </Box>
      )}

      {/* Seller performance table */}
      <Box
        sx={{
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          overflow: 'hidden',
          boxShadow: SHADOW,
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: NAVY }}>
            Rendimiento por vendedor
          </Typography>
        </Box>
        <DataGrid<SellerSummary>
          rows={sellers}
          columns={columns}
          getRowId={(row: GridRowParams<SellerSummary>['row']) => row.userId}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          localeText={{}}
          sx={{
            border: 'none',
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.85rem',
            '& .MuiDataGrid-columnHeader': { backgroundColor: SURFACE_CONTAINER_LOW },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: NAVY,
              fontSize: '0.8rem',
              letterSpacing: '0.02em',
            },
            '& .MuiDataGrid-filler': { backgroundColor: SURFACE_CONTAINER_LOW },
            '& .MuiDataGrid-cell': { color: NAVY, borderBottom: 'none', fontSize: '0.85rem' },
            '& .MuiDataGrid-row': {
              transition: 'background-color 0.15s ease',
              '&:nth-of-type(odd)': { backgroundColor: 'rgba(244, 242, 255, 0.4)' },
              '&:hover': { backgroundColor: SURFACE_HOVER },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
              backgroundColor: SURFACE_CONTAINER_LOW,
            },
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
          }}
        />
      </Box>
    </Box>
  );
};

export default BusinessUnitAdminView;
